import { useRef, useState, useEffect, useMemo } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { REGIONS } from "@/lib/clinicData";

const IDS = ["head", "torso", "arm_left", "arm_right", "leg_left", "leg_right"];

const BASE = new THREE.Color("#E9D2CA");
const HOVER = new THREE.Color("#F19075");
const PICK = new THREE.Color("#E2604A");

const FOCUS = {
  head: { y: 1.32, z: 2.5 },
  torso: { y: 0.45, z: 4.2 },
  arm_left: { y: 0.35, z: 4.4 },
  arm_right: { y: 0.35, z: 4.4 },
  leg_left: { y: -0.95, z: 4.4 },
  leg_right: { y: -0.95, z: 4.4 },
  all: { y: 0, z: 6.1 },
};

function useBodyGeometries() {
  const [geos, setGeos] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const man = await fetch("/models/body.json").then((r) => r.json());
      const out = {};
      await Promise.all(
        man.regions.map(async (r) => {
          const buf = await fetch(`/models/body-${r.id}.bin`).then((x) => x.arrayBuffer());
          const vb = r.verts * 3;
          const pos = new Float32Array(buf, 0, vb);
          const nor = new Float32Array(buf, vb * 4, vb);
          const idx = new Uint16Array(buf, vb * 8, r.tris * 3);
          const g = new THREE.BufferGeometry();
          g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
          g.setAttribute("normal", new THREE.BufferAttribute(nor, 3));
          g.setIndex(new THREE.BufferAttribute(idx, 1));
          out[r.id] = g;
        })
      );
      if (alive) setGeos(out);
    })().catch(() => setGeos({}));
    return () => { alive = false; };
  }, []);
  return geos;
}

function Region({ id, geometry, hovered, picked, anyPicked, onHover, onPick }) {
  const ref = useRef();
  useFrame((_, d) => {
    const m = ref.current?.material;
    if (!m) return;
    const k = Math.min(1, d * 7);
    m.color.lerp(picked ? PICK : hovered ? HOVER : BASE, k);
    const wantOp = anyPicked && !picked ? 0.34 : 1;
    m.opacity += (wantOp - m.opacity) * k;
    const wantEm = picked ? 0.34 : hovered ? 0.2 : 0.02;
    m.emissiveIntensity += (wantEm - m.emissiveIntensity) * k;
  });
  return (
    <mesh
      ref={ref}
      geometry={geometry}
      onPointerOver={(e) => { e.stopPropagation(); onHover(id); }}
      onPointerOut={(e) => { e.stopPropagation(); onHover(null); }}
      onClick={(e) => { e.stopPropagation(); onPick(id); }}
    >
      <meshStandardMaterial
        color={BASE}
        emissive={PICK}
        emissiveIntensity={0.02}
        roughness={0.46}
        metalness={0.05}
        transparent
        opacity={1}
        depthWrite
      />
    </mesh>
  );
}

function Rig({ rot, cam, children }) {
  const group = useRef();
  const { camera } = useThree();
  const cur = useRef({ x: 0, y: 0 });
  useFrame((_, d) => {
    const k = Math.min(1, d * 4.5);
    cur.current.y += (rot.current.y - cur.current.y) * k;
    cur.current.x += (rot.current.x - cur.current.x) * k;
    if (group.current) {
      group.current.rotation.y = cur.current.y;
      group.current.rotation.x = cur.current.x;
    }
    camera.position.z += (cam.current.z - camera.position.z) * k;
    camera.position.y += (cam.current.y - camera.position.y) * k;
    camera.lookAt(0, camera.position.y, 0);
  });
  return <group ref={group}>{children}</group>;
}

export default function BodyModel({ picked, onPick, onHoverChange }) {
  const geos = useBodyGeometries();
  const rot = useRef({ x: 0, y: 0 });
  const cam = useRef({ z: 6.1, y: 0 });
  const [hovered, setHovered] = useState(null);
  const [view, setView] = useState("front");
  const drag = useRef(null);
  const wrap = useRef(null);

  useEffect(() => {
    const f = FOCUS[picked?.id] || FOCUS.all;
    cam.current = { ...f };
  }, [picked]);

  const hover = (id) => { setHovered(id); onHoverChange?.(id); };

  const spin = (key, y) => {
    setView(key);
    const c = rot.current.y, two = Math.PI * 2;
    let n = y;
    while (n - c > Math.PI) n -= two;
    while (c - n > Math.PI) n += two;
    rot.current.y = n;
    rot.current.x = 0;
  };

  const down = (e) => { drag.current = { x: e.clientX, y: e.clientY, ry: rot.current.y, rx: rot.current.x, moved: 0 }; };
  const move = (e) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x, dy = e.clientY - drag.current.y;
    drag.current.moved = Math.abs(dx) + Math.abs(dy);
    rot.current.y = drag.current.ry + dx * 0.008;
    rot.current.x = Math.max(-0.35, Math.min(0.35, drag.current.rx + dy * 0.003));
  };
  const up = () => { drag.current = null; };

  useEffect(() => {
    const wheel = (e) => {
      if (!wrap.current?.contains(e.target)) return;
      e.preventDefault();
      cam.current.z = Math.max(1.9, Math.min(7.4, cam.current.z + e.deltaY * 0.004));
    };
    window.addEventListener("wheel", wheel, { passive: false });
    return () => window.removeEventListener("wheel", wheel);
  }, []);

  const label = useMemo(() => REGIONS.find((r) => r.id === hovered)?.label, [hovered]);

  const VIEWS = [["front", 0], ["left", -Math.PI / 2], ["back", Math.PI], ["right", Math.PI / 2]];

  return (
    <div ref={wrap} className="relative w-full h-full select-none" data-testid="body-canvas-wrap">
      <div className="absolute inset-0" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} style={{ touchAction: "none" }}>
        <Canvas camera={{ position: [0, 0, 6.1], fov: 34 }} dpr={[1, 2]} gl={{ antialias: true, alpha: true }}>
          <ambientLight intensity={0.5} />
          <directionalLight position={[3, 4, 5]} intensity={1.7} color="#FFEDE4" />
          <directionalLight position={[-5, 1, -3]} intensity={0.9} color="#FF7A55" />
          <directionalLight position={[0, -3, 2]} intensity={0.35} color="#FFB199" />
          <pointLight position={[0, 0.8, 2.6]} intensity={3} distance={7} color="#E2604A" />
          {geos && (
            <Rig rot={rot} cam={cam}>
              {IDS.filter((id) => geos[id]).map((id) => (
                <Region
                  key={id}
                  id={id}
                  geometry={geos[id]}
                  hovered={hovered === id}
                  picked={picked?.id === id}
                  anyPicked={!!picked}
                  onHover={hover}
                  onPick={(rid) => { if ((drag.current?.moved || 0) < 6) onPick(rid); }}
                />
              ))}
            </Rig>
          )}
        </Canvas>
      </div>

      {!geos && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[11px] tracking-[0.24em] uppercase text-white/30 pulse-soft">loading the body</span>
        </div>
      )}

      <div className="absolute left-3 top-3 flex sm:flex-col gap-1.5 z-10">
        {VIEWS.map(([k, y]) => (
          <button
            key={k}
            onClick={() => spin(k, y)}
            className={`rounded-full px-3 py-1.5 text-[10px] font-semibold tracking-[0.14em] uppercase transition-colors ${view === k ? "bg-[#F5D6CB] text-[#6E1B12]" : "bg-white/8 text-white/50 hover:bg-white/16"}`}
            data-testid={`body-view-${k}`}
          >
            {k}
          </button>
        ))}
      </div>

      <div className="absolute right-3 top-3 flex sm:flex-col gap-1.5 z-10">
        <button onClick={() => (cam.current.z = Math.max(1.9, cam.current.z - 0.8))} className="w-8 h-8 rounded-full bg-white/8 text-white/55 hover:bg-white/16 text-sm" data-testid="body-zoom-in">+</button>
        <button onClick={() => { cam.current = { ...FOCUS.all }; rot.current = { x: 0, y: 0 }; setView("front"); }} className="w-8 h-8 rounded-full bg-white/8 text-white/55 hover:bg-white/16 text-[9px]" data-testid="body-zoom-reset">all</button>
        <button onClick={() => (cam.current.z = Math.min(7.4, cam.current.z + 0.8))} className="w-8 h-8 rounded-full bg-white/8 text-white/55 hover:bg-white/16 text-sm" data-testid="body-zoom-out">−</button>
      </div>

      <div className="absolute left-0 right-0 bottom-3 flex justify-center px-4 pointer-events-none z-10">
        <div
          className={`rounded-full px-4 py-2 text-xs font-semibold transition-all duration-300 ${hovered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}
          style={{ background: "#F7E3DC", color: "#7A1E12" }}
          data-testid="body-hover-label"
        >
          {label || ""}
        </div>
      </div>
      <p className="absolute left-0 right-0 bottom-3 text-center text-[9px] tracking-[0.2em] uppercase text-white/25 pointer-events-none" style={{ opacity: hovered ? 0 : 1, transition: "opacity 0.3s" }}>
        drag to spin · scroll to zoom · tap a part
      </p>
    </div>
  );
}
